import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, AlertCircle, FileText, ChevronRight, Target, ThumbsUp, XCircle } from 'lucide-react';

interface Requisition {
  id: string;
  title: string;
  department: string;
  status: string;
  createdAt: string;
  closedAt?: string;
}

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  pending: { label: 'На рассмотрении', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: <Clock size={16} /> },
  approved: { label: 'Утверждена', color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: <CheckCircle size={16} /> },
  rejected: { label: 'Отклонена', color: '#ef4444', bg: 'rgba(239,68,68,0.1)', icon: <XCircle size={16} /> },
  draft: { label: 'Черновик', color: '#64748b', bg: 'rgba(100,116,139,0.1)', icon: <FileText size={16} /> },
  closed: { label: 'Закрыта', color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: <CheckCircle size={16} /> },
};

const Requisitions: React.FC = () => {
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState<Requisition[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequisitions = async () => {
      try {
        const res = await api.get('/requisitions');
        const data = res.data.map((r: any) => ({ ...r, id: r.id || r._id }));
        setRequisitions(data);
      } catch (err) {
        setError('Не удалось загрузить заявки.');
      } finally {
        setLoading(false);
      }
    };
    fetchRequisitions();
  }, []);

  const filtered = useMemo(() => {
    return filterStatus === 'all' ? requisitions : requisitions.filter(r => r.status === filterStatus);
  }, [requisitions, filterStatus]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Загрузка заявок...</div>;
  if (error) return <div style={{ padding: 60, textAlign: 'center', color: '#ef4444' }}><p>{error}</p></div>;

  const total = requisitions.length;
  const pending = requisitions.filter(r => r.status === 'pending').length;
  const approved = requisitions.filter(r => r.status === 'approved').length;
  const rejected = requisitions.filter(r => ['rejected', 'closed'].includes(r.status)).length;

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 8px 20px rgba(106,17,203,0.3)' }}>
          <FileText size={24} />
        </div>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>Заявки на подбор</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: 14 }}>Согласование и контроль открытых заявок</p>
        </div>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(37,117,252,0.1)', color: '#2575fc' }}><FileText size={22} /></div>
          <div><div style={styles.kpiLabel}>Всего</div><div style={styles.kpiValue}>{total}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}><Clock size={22} /></div>
          <div><div style={styles.kpiLabel}>На рассмотрении</div><div style={styles.kpiValue}>{pending}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}><ThumbsUp size={22} /></div>
          <div><div style={styles.kpiLabel}>Утверждено</div><div style={styles.kpiValue}>{approved}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}><XCircle size={22} /></div>
          <div><div style={styles.kpiLabel}>Отклонено/Закрыто</div><div style={styles.kpiValue}>{rejected}</div></div>
        </div>
      </div>

      {/* Фильтры-таблетки */}
      <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.5)', padding: 4, borderRadius: 12, border: '1px solid rgba(255,255,255,0.6)', marginBottom: 20, flexWrap: 'wrap', alignSelf: 'flex-start', width: 'fit-content' }}>
        <button onClick={() => setFilterStatus('all')} style={{ ...styles.filterBtn, background: filterStatus === 'all' ? '#2575fc' : 'transparent', color: filterStatus === 'all' ? '#fff' : '#64748b' }}>Все</button>
        {Object.entries(statusConfig).map(([key, cfg]) => (
          <button key={key} onClick={() => setFilterStatus(key)} style={{ ...styles.filterBtn, background: filterStatus === key ? '#2575fc' : 'transparent', color: filterStatus === key ? '#fff' : '#64748b' }}>{cfg.label}</button>
        ))}
      </div>

      {/* Таблица */}
      <div style={styles.glassCard}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={styles.th}>Позиция</th>
                <th style={styles.th}>Отдел</th>
                <th style={styles.th}>Статус</th>
                <th style={styles.th}>Дата создания</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Нет заявок</td></tr>
              ) : (
                filtered.map(req => {
                  const config = statusConfig[req.status] || statusConfig.draft;
                  return (
                    <tr key={req.id} onClick={() => navigate(`/app/recruitment-analytics/requisitions/${req.id}`)} style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(37,117,252,0.03)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <td style={styles.td}><strong>{req.title}</strong></td>
                      <td style={styles.td}>{req.department || '—'}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: config.bg, color: config.color }}>
                          {config.icon} {config.label}
                        </span>
                      </td>
                      <td style={styles.td}>{new Date(req.createdAt).toLocaleDateString('ru-RU')}</td>
                      <td style={styles.td}><ChevronRight size={16} color="#94a3b8" /></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)', overflow: 'hidden' },
  kpiCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', display: 'flex', alignItems: 'center', gap: 16 },
  kpiIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 },
  kpiValue: { fontSize: 24, fontWeight: 800, color: '#1a1a2e' },
  filterBtn: { padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, transition: 'all 0.2s' },
  th: { textAlign: 'left', padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  td: { padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.03)', fontSize: 14, color: '#1a1a2e' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600 },
};

export default Requisitions;
